import { MoreOutlined } from "@ant-design/icons";
import React, { useState } from "react";
import "./NotificationsScreen.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Loader from "../../../components/element/Loader";
import SnackbarAlert from "../../../components/element/SnackbarAlert";
import ROUTE_PATHS from "../../../utils/routePaths";
import { Link } from "react-router-dom";
import { PAGINATION_PAGE_SIZE } from "../../../utils/constants";
import useAirlineAdminApi from "../../../services/api/useAirlineAdminApi";
import CommercialAirlineNavigationBar from "../../../components/element/CommercialAirlineNavigationBar";
import { useFormData } from "../../../helpers/FormDataProvider";

const NotificationsScreen = () => {
  const { userData } = useFormData();
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = PAGINATION_PAGE_SIZE;
  const {
    fetchAllNotifications,
    readNotifications,
    unreadNotification,
    deleteNotification,
    readAllNotifications,
  } = useAirlineAdminApi(userData.isAAUser);
  const [filter, setFilter] = useState("all");
  const [activeMenu, setActiveMenu] = useState(null);
  const [actionCount, setActionCount] = useState(0);
  const queryClient = useQueryClient();

  const {
    data: notificationsData,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["fbo-notifications-list", currentPage, actionCount],
    queryFn: () =>
      fetchAllNotifications({
        page: currentPage,
        size: pageSize,
      }),
    retry: false,
  });

  const readNotificationMutation = useMutation({
    mutationFn: ({ notificationId }) => readNotifications(notificationId),
    onSuccess: () => {
      setActionCount(actionCount + 1);
      setActiveMenu(null);
    },
  });

  const unreadNotificationMutation = useMutation({
    mutationFn: ({ notificationId }) => unreadNotification(notificationId),
    onSuccess: () => {
      setActionCount(actionCount + 1);
      setActiveMenu(null);
    },
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: ({ notificationId }) => deleteNotification(notificationId),
    onSuccess: () => {
      setActionCount(actionCount + 1);
      setActiveMenu(null);
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => readAllNotifications(),
    onSuccess: () => {
      setActionCount((prev) => prev + 1);
      setActiveMenu(null);
      // Refresh unread badge count in the top navigation
      queryClient.invalidateQueries({ queryKey: ["fbo-notifications-count"] });
    },
  });

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const hasAnyNotifications = notificationsData?.data?.content?.length > 0;

  const getFilteredNotifications = () => {
    if (filter === "all") return notificationsData?.data?.content;
    if (filter === "read")
      return notificationsData?.data?.content?.filter(
        (notification) => notification.isRead
      );
    if (filter === "unread")
      return notificationsData?.data?.content?.filter(
        (notification) => !notification.isRead
      );
    return notificationsData?.data?.content;
  };

  const toggleMenu = (index) => {
    setActiveMenu(activeMenu === index ? null : index);
  };

  const handleFilterChange = (filterType) => {
    if (filter === filterType) {
      setFilter("all");
    } else {
      setFilter(filterType);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    readNotificationMutation.mutate({
      notificationId: notificationId,
    });
  };

  const handleMarkAsUnread = async (notificationId) => {
    unreadNotificationMutation.mutate({
      notificationId: notificationId,
    });
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      readNotificationMutation.mutate({
        notificationId: notification?.id,
      });
    }
  };

  const handleDelete = async (notificationId) => {
    deleteNotificationMutation.mutate({
      notificationId: notificationId,
    });
  };

  const handleNextPage = () => {
    if (currentPage + 1 < notificationsData?.data?.totalPages) {
      setCurrentPage((prev) => prev + 1);
      setActiveMenu(null);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
      setActiveMenu(null);
    }
  };

  const getLoadingStatus = () => {
    return (
      isLoading ||
      readNotificationMutation?.isPending ||
      unreadNotificationMutation?.isPending ||
      deleteNotificationMutation?.isPending ||
      markAllAsReadMutation?.isPending
    );
  };

  return (
    <div>
      {markAllAsReadMutation.isSuccess ? (
        <SnackbarAlert type={"success"}>
          All notifications marked as read.
        </SnackbarAlert>
      ) : (
        ""
      )}
      {markAllAsReadMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {markAllAsReadMutation.error?.message ||
            "Failed to mark notifications as read."}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <CommercialAirlineNavigationBar
        title={"Notifications"}
        fullWidthContent={true}
        backContent={
          <div className="navigation-header">
            <Link
              to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
              className="navigation-header-link"
            >
              Home/
            </Link>
            Notifications
          </div>
        }
      />
      <div className="notification-screen-filters">
        <div className="notification-screen-filter-options">
          <div className="notification-screen-filter-options-left">
            <label className="notification-screen-checkbox-container">
              <input
                type="radio"
                name="filter"
                checked={filter === "all"}
                onChange={() => handleFilterChange("all")}
              />
              All
            </label>
            <div className="notification-screen-checkbox-container">
              <input
                type="radio"
                name="filter"
                checked={filter === "read"}
                onChange={() => handleFilterChange("read")}
              />
              Read
            </div>
            <div className="notification-screen-checkbox-container">
              <input
                type="radio"
                name="filter"
                checked={filter === "unread"}
                onChange={() => handleFilterChange("unread")}
              />
              Unread
            </div>
          </div>
          <button
            type="button"
            className="notification-screen-mark-all-button"
            onClick={handleMarkAllAsRead}
            disabled={markAllAsReadMutation?.isPending || !hasAnyNotifications}
          >
            Mark All as Read
          </button>
        </div>
      </div>

      <div className="notification-screen-list">
        {getLoadingStatus() ? (
          <Loader />
        ) : error && error.status === 404 ? (
          <div className="notification-screen-no-data">
            No notifications founds
          </div>
        ) : notificationsData?.data && notificationsData?.data?.length === 0 ? (
          <div className="notification-screen-no-data">
            No notifications found
          </div>
        ) : (
          <div>
            <div>
              {getFilteredNotifications()?.map((item, index) => (
                <div
                  key={item?.id}
                  className={`notification-screen-item ${item?.isRead ? "" : "notification-unread"
                    }`}
                >
                  <div
                    className="notification-screen-content"
                    onClick={() => handleNotificationClick(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="notification-message">
                      {item?.notification}
                    </div>
                    <div className="notification-timestamp">
                      {dayjs(item?.createdAt).format("MMM D, YYYY HH:mm")}
                    </div>
                  </div>
                  <div className="notification-screen-actions">
                    <div className="notification-screen-menu">
                      <button
                        className="notification-screen-menu-button"
                        onClick={() => toggleMenu(index)}
                      >
                        <MoreOutlined rotate={90} />
                      </button>
                      {activeMenu === index && (
                        <div className="notification-screen-dropdown">
                          {!item?.isRead && (
                            <div
                              className="notification-screen-menu-item"
                              onClick={() => handleMarkAsRead(item?.id)}
                            >
                              Mark as Read
                            </div>
                          )}
                          {item?.isRead && (
                            <div
                              className="notification-screen-menu-item"
                              onClick={() => handleMarkAsUnread(item?.id)}
                            >
                              Mark as Unread
                            </div>
                          )}
                          <div
                            className="notification-screen-menu-item"
                            onClick={() => handleDelete(item?.id)}
                          >
                            Delete
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {notificationsData?.data?.content &&
        notificationsData?.data?.content.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-content">
              Page {currentPage + 1} of {notificationsData?.data?.totalPages}
            </div>
            <button
              className="pagination-button"
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
            >
              <CaretLeftOutlined />
            </button>
            <button
              className="pagination-button"
              onClick={handleNextPage}
              disabled={currentPage + 1 === notificationsData?.data?.totalPages}
            >
              <CaretRightOutlined />
            </button>
          </div>
        )}
    </div>
  );
};

export default NotificationsScreen;
